import React, { useEffect, useRef, useState } from 'react';
import { getMezzi, createMezzo, updateMezzo, deleteMezzo, deleteMezzoDefinitivo, exportMezziXlsx, importMezziXlsx, Mezzo } from '../../services/fleet';
import api, { mezziApi } from '../../services/api';

interface StoricoAssegnazioneMezzo {
  id: number;
  data_assegnazione: string;
  tecnico_nome?: string | null;
  commessa_nome?: string | null;
  cantiere_nome?: string | null;
  assegnato_da_nome?: string | null;
  note?: string | null;
  km_presa_consegna?: number | null;
  km_riconsegna?: number | null;
  danni_presa_consegna?: string | null;
  danni_riconsegna?: string | null;
  created_at: string;
}

function formatDataIt(d?: string | null): string {
  if (!d) return '-';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString('it-IT');
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TIPI_VEICOLO = ['autovettura', 'autocarro', 'furgone', 'autobus', 'motociclo'];
const TIPI_PROPRIETA = ['acquisto', 'leasing', 'noleggio', 'comodato'];
const STATUS_VEICOLO = [
  'disponibile', 'in_uso', 'in_riparazione',
  'venduto', 'demolito', 'sequestrato', 'parcheggiato',
];
const ALIMENTAZIONE = ['diesel', 'benzina', 'gpl', 'metano', 'elettrico', 'ibrido'];

const STATUS_COLORS: Record<string, string> = {
  disponibile:    '#22c55e',
  in_uso:         '#3b82f6',
  in_riparazione: '#f59e0b',
  venduto:        '#6b7280',
  demolito:       '#374151',
  sequestrato:    '#ef4444',
  parcheggiato:   '#8b5cf6',
};

const EMPTY: Partial<Mezzo> = {
  targa: '', marca: '', modello: '', anno: undefined,
  tipo_veicolo: 'furgone', tipo_proprieta: 'acquisto',
  status_veicolo: 'disponibile', alimentazione: 'diesel',
  km: undefined, note: '',
};

export default function AnagraficaMezzi() {
  const [mezzi, setMezzi] = useState<Mezzo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Mezzo | null>(null);
  const [form, setForm] = useState<Partial<Mezzo>>(EMPTY);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPropr, setFilterPropr] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  const [tecnici, setTecnici] = useState<{ id: number; nome: string; cognome: string }[]>([]);
  const [tecnicoInput, setTecnicoInput] = useState('');
  const [storicoMezzo, setStoricoMezzo] = useState<Mezzo | null>(null);
  const [storico, setStorico] = useState<StoricoAssegnazioneMezzo[]>([]);
  const [storicoLoading, setStoricoLoading] = useState(false);
  const [storicoError, setStoricoError] = useState('');

  const handleExport = async () => {
    try {
      const res = await exportMezziXlsx(false);
      downloadBlob(res.data as Blob, 'fleet_veicoli.xlsx');
    } catch { setError('Errore export'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importMezziXlsx(file);
      const { created, updated, errors } = res.data;
      alert(`Import completato: ${created} creati, ${updated} aggiornati${errors.length ? '\nErrori: ' + errors.join('\n') : ''}`);
      load();
    } catch { setError('Errore import'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMezzi(false);
      setMezzi(res.data);
    } catch {
      setError('Errore caricamento veicoli');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get('/tecnici', { params: { limit: 500, is_active: true } })
      .then(r => setTecnici(r.data?.items ?? r.data ?? []))
      .catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setTecnicoInput(''); setError(''); setShowForm(true); };
  const openEdit = (m: Mezzo) => {
    setEditing(m);
    setForm({ ...m });
    setTecnicoInput(m.tecnico_nome || m.tecnico_assegnato_nome_provvisorio || '');
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.targa?.trim()) { setError('Targa obbligatoria'); return; }
    setSaving(true);
    setError('');
    try {
      const tecName = tecnicoInput.trim();
      const matched = tecName
        ? tecnici.find(t => `${t.nome} ${t.cognome}`.toLowerCase() === tecName.toLowerCase())
        : null;
      const payload: Partial<Mezzo> = {
        ...form,
        tecnico_assegnato_id: matched ? matched.id : undefined,
        tecnico_assegnato_nome_provvisorio: !matched && tecName ? tecName : undefined,
      };
      if (editing) {
        await updateMezzo(editing.id, payload);
      } else {
        await createMezzo(payload);
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Disattivare questo veicolo?')) return;
    try {
      await deleteMezzo(id);
      load();
    } catch {
      setError('Errore eliminazione');
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await updateMezzo(id, { is_active: true });
      load();
    } catch {
      setError('Errore attivazione');
    }
  };

  const openStorico = async (m: Mezzo) => {
    setStoricoMezzo(m);
    setStorico([]);
    setStoricoError('');
    setStoricoLoading(true);
    try {
      const res = await mezziApi.storicoAssegnazioni(m.id);
      setStorico(res.data || []);
    } catch {
      setStoricoError('Errore caricamento storico assegnazioni');
    } finally {
      setStoricoLoading(false);
    }
  };

  const handleDeleteDefinitivo = async (id: number, targa: string) => {
    if (!confirm(`Rimuovere definitivamente il veicolo ${targa}? L'operazione è irreversibile.`)) return;
    try {
      await deleteMezzoDefinitivo(id);
      load();
    } catch {
      setError('Errore rimozione definitiva');
    }
  };

  const filtered = mezzi.filter(m => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      m.targa.toLowerCase().includes(q) ||
      `${m.marca || ''} ${m.modello || ''}`.toLowerCase().includes(q);
    const matchStatus = !filterStatus || m.status_veicolo === filterStatus;
    const matchPropr  = !filterPropr  || m.tipo_proprieta === filterPropr;
    return matchSearch && matchStatus && matchPropr;
  });

  const set = (patch: Partial<Mezzo>) => setForm(f => ({ ...f, ...patch }));

  return (
    <div className="fleet-section">
      <div className="fleet-toolbar">
        <input
          className="fleet-search"
          placeholder="Cerca targa o modello..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select className="fleet-filter" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Tutti gli status</option>
          {STATUS_VEICOLO.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <select className="fleet-filter" value={filterPropr} onChange={e => setFilterPropr(e.target.value)}>
          <option value="">Tutti i tipi proprietà</option>
          {TIPI_PROPRIETA.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <button className="btn-secondary" onClick={handleExport} title="Scarica XLSX">⬇ XLSX</button>
        <button className="btn-secondary" onClick={() => importRef.current?.click()} disabled={importing} title="Carica XLSX">
          {importing ? '...' : '⬆ XLSX'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
        <button className="btn-primary" onClick={openNew}>+ Nuovo Veicolo</button>
      </div>

      {error && <div className="fleet-error">{error}</div>}

      {loading ? (
        <div className="fleet-loading">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="fleet-stub">Nessun veicolo trovato</div>
      ) : (
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Targa</th>
              <th>Veicolo</th>
              <th>Anno</th>
              <th>Tipo</th>
              <th>Proprietà</th>
              <th>Status</th>
              <th>KM</th>
              <th>Tecnico assegnatario</th>
              <th>⚠</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.id} className={!m.is_active ? 'row-inactive' : ''}>
                <td><strong>{m.targa}</strong></td>
                <td>{[m.marca, m.modello].filter(Boolean).join(' ') || '—'}</td>
                <td>{m.anno || '—'}</td>
                <td>{m.tipo_veicolo || m.tipo || '—'}</td>
                <td>{m.tipo_proprieta || '—'}</td>
                <td>
                  <span
                    className="fleet-badge"
                    style={{ background: STATUS_COLORS[m.status_veicolo || ''] || '#6b7280' }}
                  >
                    {(m.status_veicolo || '—').replace(/_/g, ' ')}
                  </span>
                </td>
                <td>{m.km ? m.km.toLocaleString('it-IT') : '—'}</td>
                <td>
                  {m.tecnico_nome
                    ? m.tecnico_nome
                    : m.tecnico_assegnato_nome_provvisorio
                      ? <span title="Tecnico non ancora registrato in sistema" style={{ color: 'var(--mac-orange)' }}>⏳ {m.tecnico_assegnato_nome_provvisorio}</span>
                      : '—'}
                </td>
                <td>
                  {(m.scadenze_in_scadenza || 0) > 0 && (
                    <span className="fleet-alert-badge">{m.scadenze_in_scadenza}</span>
                  )}
                </td>
                <td>
                  <button className="btn-icon" onClick={() => openEdit(m)} title="Modifica">✏️</button>
                  <button className="btn-icon" onClick={() => openStorico(m)} title="Storico assegnazioni">🕒</button>
                  {m.is_active ? (
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(m.id)} title="Disattiva">🗑️</button>
                  ) : (
                    <button className="btn-icon btn-success" onClick={() => handleActivate(m.id)} title="Attiva">✅</button>
                  )}
                  <button className="btn-icon btn-danger" onClick={() => handleDeleteDefinitivo(m.id, m.targa)} title="Rimuovi definitivamente">❌</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="fleet-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="fleet-modal" onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>{editing ? `Modifica: ${editing.targa}` : 'Nuovo Veicolo'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>

            <div className="fleet-modal-body">
              {error && <div className="fleet-error">{error}</div>}

              <div className="form-section-title">Identificazione</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Targa *</label>
                  <input
                    value={form.targa || ''}
                    onChange={e => set({ targa: e.target.value.toUpperCase() })}
                    placeholder="AB123CD"
                  />
                </div>
                <div className="form-group">
                  <label>Marca</label>
                  <input value={form.marca || ''} onChange={e => set({ marca: e.target.value })} placeholder="Fiat, Iveco, Mercedes..." />
                </div>
                <div className="form-group">
                  <label>Modello</label>
                  <input value={form.modello || ''} onChange={e => set({ modello: e.target.value })} placeholder="Daily, Sprinter..." />
                </div>
                <div className="form-group">
                  <label>Anno</label>
                  <input type="number" value={form.anno || ''} onChange={e => set({ anno: parseInt(e.target.value) || undefined })} placeholder="2022" />
                </div>
                <div className="form-group">
                  <label>Colore</label>
                  <input value={form.colore || ''} onChange={e => set({ colore: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Telaio (VIN)</label>
                  <input value={form.telaio || ''} onChange={e => set({ telaio: e.target.value.toUpperCase() })} />
                </div>
              </div>

              <div className="form-section-title">Caratteristiche</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Tipo veicolo</label>
                  <select value={form.tipo_veicolo || ''} onChange={e => set({ tipo_veicolo: e.target.value })}>
                    {TIPI_VEICOLO.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Alimentazione</label>
                  <select value={form.alimentazione || ''} onChange={e => set({ alimentazione: e.target.value })}>
                    {ALIMENTAZIONE.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Cilindrata (cc)</label>
                  <input type="number" value={form.cilindrata || ''} onChange={e => set({ cilindrata: parseInt(e.target.value) || undefined })} />
                </div>
                <div className="form-group">
                  <label>KM attuali</label>
                  <input type="number" value={form.km || ''} onChange={e => set({ km: parseInt(e.target.value) || undefined })} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status_veicolo || 'disponibile'} onChange={e => set({ status_veicolo: e.target.value })}>
                    {STATUS_VEICOLO.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stato carrozzeria</label>
                  <input value={form.stato_carrozzeria || ''} onChange={e => set({ stato_carrozzeria: e.target.value })} placeholder="Buono, Ammaccatura laterale..." />
                </div>
              </div>

              <div className="form-section-title">Proprietà & Contratto</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Tipo proprietà</label>
                  <select value={form.tipo_proprieta || ''} onChange={e => set({ tipo_proprieta: e.target.value })}>
                    {TIPI_PROPRIETA.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Fornitore / Concessionaria</label>
                  <input value={form.fornitore_nome || ''} onChange={e => set({ fornitore_nome: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Valore acquisto (€)</label>
                  <input type="number" step="0.01" value={form.valore_acquisto || ''} onChange={e => set({ valore_acquisto: parseFloat(e.target.value) || undefined })} />
                </div>
                <div className="form-group">
                  <label>Data acquisto</label>
                  <input type="date" value={form.data_acquisto || ''} onChange={e => set({ data_acquisto: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Data immatricolazione</label>
                  <input type="date" value={form.data_immatricolazione || ''} onChange={e => set({ data_immatricolazione: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Fine contratto (leasing/noleggio)</label>
                  <input type="date" value={form.fine_contratto || ''} onChange={e => set({ fine_contratto: e.target.value })} />
                </div>
              </div>

              <div className="form-section-title">Assegnazione Tecnico</div>
              <div className="form-group">
                <label>Tecnico assegnatario</label>
                <input
                  list="tecnici-datalist"
                  value={tecnicoInput}
                  onChange={e => setTecnicoInput(e.target.value)}
                  placeholder="Digita nome o lascia vuoto per nessuna assegnazione"
                />
                <datalist id="tecnici-datalist">
                  {tecnici.map(t => (
                    <option key={t.id} value={`${t.nome} ${t.cognome}`} />
                  ))}
                </datalist>
                {tecnicoInput.trim() && !tecnici.find(t =>
                  `${t.nome} ${t.cognome}`.toLowerCase() === tecnicoInput.trim().toLowerCase()
                ) && (
                  <p style={{ color: 'var(--mac-orange)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    ⏳ Tecnico non trovato — verrà salvato come collegamento provvisorio
                  </p>
                )}
              </div>

              <div className="form-section-title">Note</div>
              <div className="form-group">
                <textarea rows={3} value={form.note || ''} onChange={e => set({ note: e.target.value })} placeholder="Note aggiuntive..." />
              </div>
            </div>

            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Crea Veicolo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {storicoMezzo && (
        <div className="fleet-modal-overlay" onClick={() => setStoricoMezzo(null)}>
          <div className="fleet-modal" onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>Storico assegnazioni: {storicoMezzo.targa}</h2>
              <button onClick={() => setStoricoMezzo(null)}>✕</button>
            </div>

            <div className="fleet-modal-body">
              {storicoError && <div className="fleet-error">{storicoError}</div>}

              {storicoLoading ? (
                <div className="fleet-loading">Caricamento...</div>
              ) : storico.length === 0 ? (
                <div className="fleet-stub">Nessuna assegnazione storicizzata per questo veicolo</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left p-2">Data</th>
                      <th className="text-left p-2">Tecnico</th>
                      <th className="text-left p-2">Commessa</th>
                      <th className="text-left p-2">Cantiere</th>
                      <th className="text-left p-2">Assegnato da</th>
                      <th className="text-left p-2">Km presa/riconsegna</th>
                      <th className="text-left p-2">Danni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storico.map(s => (
                      <tr key={s.id} className="border-t border-[var(--border)]">
                        <td className="p-2">{formatDataIt(s.data_assegnazione)}</td>
                        <td className="p-2">{s.tecnico_nome || '-'}</td>
                        <td className="p-2">{s.commessa_nome || '-'}</td>
                        <td className="p-2">{s.cantiere_nome || '-'}</td>
                        <td className="p-2">{s.assegnato_da_nome || '-'}</td>
                        <td className="p-2">{s.km_presa_consegna ?? '-'} / {s.km_riconsegna ?? '-'}</td>
                        <td className="p-2">{[s.danni_presa_consegna, s.danni_riconsegna].filter(Boolean).join(' | ') || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setStoricoMezzo(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
